/**
 * Unified Navigation Service for Authentication Flow
 * Provides centralized control over phase transitions with validation,
 * history tracking, error handling, and recovery support.
 */

import { ref } from 'vue';
import type { AuthPhase } from '@/types/profileTypes';
import { authPhaseConfigs } from '@/utils/auth-phase-config';
import { saveEnhancedAuthState } from '@/utils/auth-state-persistence';
import type { AuthOrchestratorState } from './useAuthState';
import {
  NO_BACK_PHASES,
  ROLE_PHASES,
  NON_RECOVERABLE_PHASES,
  ERROR_MESSAGES,
  QUEUE_LIMITS,
  UI_TIMEOUTS
} from '../constants';
import { createDebugLogger } from '../utils/debug';

const logger = createDebugLogger('NavigationService');

// Define NavigationOptions before NavigationService to satisfy no-use-before-define
interface NavigationOptions {
  skipValidation?: boolean;
  skipHistory?: boolean;
  skipPersistence?: boolean;
  direction?: 'forward' | 'back';
}

export interface NavigationService {
  // Standard navigation with validation
  navigate: (phase: AuthPhase, options?: NavigationOptions) => Promise<boolean>;

  // System transitions (special handling)
  systemTransition: (phase: 'idle' | 'error' | 'complete') => Promise<void>;

  // Error handling with automatic rollback
  transitionWithError: (error: string) => Promise<void>;

  // Complete the flow with cleanup
  completeFlow: (redirect?: string) => Promise<void>;

  // Role-specific navigation
  navigateToRole: (roleType: string) => Promise<boolean>;

  // Recovery and rollback
  rollback: () => Promise<void>;
  canNavigateTo: (phase: AuthPhase) => boolean;

  // Utility functions
  getCurrentPhase: () => AuthPhase;
  getPreviousPhase: () => AuthPhase;
  getPhaseHistory: () => AuthPhase[];

  // Cleanup function to prevent memory leaks
  cleanup: () => void;
}

export function useAuthNavigationService(
  state: AuthOrchestratorState,
  deps?: {
    onComplete?: (redirect?: string) => void;
    onError?: (error: string) => void;
  }
): NavigationService {
  // Module-scoped navigation state to prevent memory leaks
  const navigationInProgress = ref(false);
  const navigationQueue = ref<Array<() => Promise<void>>>([]);
  let processQueueTimeout: NodeJS.Timeout | null = null;

  /**
   * Process navigation queue to handle concurrent requests
   */
  const processNavigationQueue = async () => {
    if (navigationInProgress.value || navigationQueue.value.length === 0) return;

    navigationInProgress.value = true;
    const nextNavigation = navigationQueue.value.shift();

    try {
      if (nextNavigation) {
        await nextNavigation();
      }
    } finally {
      navigationInProgress.value = false;
      // Process next item in queue with cleanup
      if (processQueueTimeout) {
        clearTimeout(processQueueTimeout);
      }
      if (navigationQueue.value.length > 0) {
        processQueueTimeout = setTimeout(processNavigationQueue, UI_TIMEOUTS.QUEUE_PROCESS_DELAY);
      }
    }
  };

  /**
   * Queue a navigation operation with size limit
   */
  const queueNavigation = <T>(operation: () => Promise<T>): Promise<T> => {
    // Handle queue overflow gracefully
    if (navigationQueue.value.length >= QUEUE_LIMITS.MAX_NAVIGATION_QUEUE_SIZE) {
      logger.warn('Navigation queue at capacity, removing oldest operation to make room');
      // Remove the oldest operation (FIFO) to prevent blocking
      navigationQueue.value.shift();
    }

    return new Promise((resolve, reject) => {
      navigationQueue.value.push(async () => {
        try {
          const result = await operation();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
      processNavigationQueue();
    });
  };

  /**
   * Validate phase transition
   */
  const validateTransition = (from: AuthPhase, to: AuthPhase): boolean => {
    // Can't transition to the same phase
    if (from === to) {
      logger.warn(`Cannot transition to same phase: ${to}`);
      return false;
    }

    // Check phase config for validation rules
    const config = authPhaseConfigs[to];
    if (config?.validator) {
      return config.validator(state);
    }

    // Check if phase is locked
    if (state.lockedPhases?.has(to)) {
      logger.warn(`Phase is locked: ${to}`);
      return false;
    }

    // Check one-way gates
    if (config?.isOneWayGate && state.passedGates?.has(to)) {
      logger.warn(`Already passed one-way gate: ${to}`);
      return false;
    }

    return true;
  };

  /**
   * Save state for recovery
   */
  const persistState = async (): Promise<void> => {
    if (NON_RECOVERABLE_PHASES.includes(state.phase)) {
      return; // Don't save sensitive phases
    }

    try {
      await saveEnhancedAuthState(state);
      logger.debug('State persisted for recovery');
    } catch (error) {
      logger.error('Failed to persist state:', error);
    }
  };

  /**
   * Main navigation function with full validation and tracking
   */
  const navigate = async (
    targetPhase: AuthPhase,
    options: NavigationOptions = {}
  ): Promise<boolean> => {
    return queueNavigation(async () => {
      const {
        skipValidation = false,
        skipHistory = false,
        skipPersistence = false,
        direction = 'forward'
      } = options;

      logger.log(`Navigating to ${targetPhase} (${direction}) from current phase: ${state.phase}`);

      // Validate transition
      if (!skipValidation && !validateTransition(state.phase, targetPhase)) {
        logger.warn(`Invalid transition from ${state.phase} to ${targetPhase}`);
        return false;
      }

      // Clear errors on navigation
      state.error = null;

      // Track history
      if (!skipHistory && direction === 'forward') {
        if (!state.phaseHistory.includes(state.phase)) {
          state.phaseHistory.push(state.phase);
        }
      }

      // Update phase
      const previousPhase = state.phase;
      state.previousPhase = previousPhase;
      state.phase = targetPhase;

      // Track encountered phases
      state.encounteredPhases.add(targetPhase);

      // Mark as completed when moving forward
      if (direction === 'forward') {
        state.completedPhases.add(previousPhase);

        // Mark one-way gates as passed when LEAVING them, not when entering
        const previousConfig = authPhaseConfigs[previousPhase];
        if (previousConfig?.isOneWayGate) {
          state.passedGates.add(previousPhase);
          logger.debug(`Marked one-way gate as passed: ${previousPhase}`);
        }
      }

      // Update max reached phase
      const phaseOrder = [
        'contact',
        'otp',
        'password',
        'setPassword',
        'alternateContact',
        'generalProfile'
      ];
      const currentIndex = phaseOrder.indexOf(targetPhase);
      const maxIndex = phaseOrder.indexOf(state.maxReachedPhase);
      if (currentIndex > maxIndex) {
        state.maxReachedPhase = targetPhase;
      }

      // Persist state for recovery
      if (!skipPersistence) {
        await persistState();
      }

      logger.log(`Successfully navigated from ${previousPhase} to ${targetPhase}`);
      return true;
    });
  };

  /**
   * System-level transitions (idle, error, complete)
   */
  const systemTransition = async (phase: 'idle' | 'error' | 'complete'): Promise<void> => {
    return queueNavigation(async () => {
      logger.log(`System transition to ${phase}`);

      // System transitions bypass most validation
      state.previousPhase = state.phase;
      state.phase = phase;

      // Special handling for each system phase
      switch (phase) {
        case 'idle':
          // Reset to initial state
          state.phaseHistory = [];
          state.completedPhases.clear();
          state.encounteredPhases.clear();
          state.passedGates.clear();
          state.error = null;
          break;

        case 'error':
          // Error state preserves history for recovery
          break;

        case 'complete':
          // Clear sensitive data on completion
          state.passedGates.clear();
          state.lockedPhases.clear();
          if (deps?.onComplete) {
            deps.onComplete();
          }
          break;

        default: {
          // TypeScript exhaustiveness check - ensures all cases are handled
          const _exhaustive: never = phase;
          throw new Error(`Unhandled system phase: ${_exhaustive}`);
        }
      }

      // Don't persist system states
      logger.log(`System transition to ${phase} complete`);
    });
  };

  /**
   * Transition to error state with message
   */
  const transitionWithError = async (error: string): Promise<void> => {
    return queueNavigation(async () => {
      logger.error(`Transitioning to error: ${error}`);

      // Save current state for potential recovery
      const savedPhase = state.phase;

      state.error = error;
      state.previousPhase = savedPhase;
      state.phase = 'error';

      if (deps?.onError) {
        deps.onError(error);
      }
    });
  };

  /**
   * Complete the authentication flow
   */
  const completeFlow = async (redirect?: string): Promise<void> => {
    return queueNavigation(async () => {
      logger.log('Completing authentication flow, redirect:', redirect);

      try {
        // Mark all selected roles as complete
        if (state.selectedUserTypes?.length > 0) {
          state.selectedUserTypes.forEach(role => {
            const rolePhase = `${role.toLowerCase()}Profile` as AuthPhase;
            state.completedPhases.add(rolePhase);
          });
        }

        // Clear loading state before transition
        state.loading = false;

        // Transition to complete (direct to avoid queue deadlock)
        logger.log('System transition to complete');
        state.previousPhase = state.phase;
        state.phase = 'complete';

        // Handle redirect through callback
        if (deps?.onComplete) {
          // Pass the redirect or use the one stored in state
          deps.onComplete(redirect || state.originalRedirect);
        }
      } catch (error) {
        logger.error('Error in completeFlow:', error);
        // Ensure loading state is cleared on error
        state.loading = false;
        throw error;
      }
    });
  };

  /**
   * Navigate to a role-specific profile phase
   */
  const navigateToRole = async (roleType: string): Promise<boolean> => {
    const rolePhase = `${roleType.toLowerCase()}Profile` as AuthPhase;

    if (!ROLE_PHASES.includes(rolePhase)) {
      logger.error(`Invalid role phase: ${rolePhase}`);
      return false;
    }

    return navigate(rolePhase);
  };

  /**
   * Rollback to previous phase
   */
  const rollback = async (): Promise<void> => {
    return queueNavigation(async () => {
      if (!state.previousPhase || state.previousPhase === state.phase) {
        logger.warn('No previous phase to rollback to');
        return;
      }

      logger.log(`Rolling back from ${state.phase} to ${state.previousPhase}`);

      const temp = state.phase;
      state.phase = state.previousPhase;
      state.previousPhase = temp;

      // Clear any errors
      state.error = null;
    });
  };

  /**
   * Check if navigation to a phase is allowed
   */
  const canNavigateTo = (targetPhase: AuthPhase): boolean => {
    return validateTransition(state.phase, targetPhase);
  };

  /**
   * Get current phase
   */
  const getCurrentPhase = (): AuthPhase => {
    return state.phase;
  };

  /**
   * Get previous phase
   */
  const getPreviousPhase = (): AuthPhase => {
    return state.previousPhase;
  };

  /**
   * Get phase history
   */
  const getPhaseHistory = (): AuthPhase[] => {
    return [...state.phaseHistory];
  };

  /**
   * Cleanup function to prevent memory leaks
   */
  const cleanup = () => {
    if (processQueueTimeout) {
      clearTimeout(processQueueTimeout);
      processQueueTimeout = null;
    }
    navigationQueue.value = [];
    navigationInProgress.value = false;
  };

  return {
    navigate,
    systemTransition,
    transitionWithError,
    completeFlow,
    navigateToRole,
    rollback,
    canNavigateTo,
    getCurrentPhase,
    getPreviousPhase,
    getPhaseHistory,
    cleanup
  };
}
